import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  X,
  LayoutGrid,
  BarChart3,
  Target,
  Receipt,
  TrendingUp,
  Sparkles,
  AlertTriangle,
  Crown,
  ArrowLeft,
  Command,
  ShieldCheck,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { track, searchSkus } from "../lib/api";
import { useChat } from "../lib/chatContext";

/**
 * Cmd+K / Ctrl+K command palette.
 * - Navigation shortcuts (Home, Insights, Action Center, Chat, Admin)
 * - Quick filters (Growers, Variance, Anomalies)
 * - Instant AI ask: any free-text query sent to chat
 *
 * Opens on Cmd/Ctrl+K, closes on Escape or backdrop click.
 */
const COMMANDS = [
  // Navigation
  { id: "nav-home", label: "דף הבית", group: "ניווט", icon: LayoutGrid, action: { type: "nav", to: "/" }, keywords: ["home", "בית"] },
  { id: "nav-insights", label: "תובנות עסקיות", group: "ניווט", icon: BarChart3, action: { type: "nav", to: "/insights" } },
  { id: "nav-inventory", label: "מלאי", group: "ניווט", icon: LayoutGrid, action: { type: "nav", to: "/inventory" }, keywords: ["inventory", "מחסן", "stock"] },
  { id: "nav-actions", label: "מרכז פעולות", group: "ניווט", icon: Target, action: { type: "nav", to: "/actions" } },
  { id: "nav-chat", label: "צ׳אט עם Optygo Pulse", group: "ניווט", icon: Sparkles, action: { type: "nav", to: "/chat" } },

  // Quick views
  { id: "view-growers", label: "פריטים בקצב גידול", group: "תצוגות מהירות", icon: TrendingUp, action: { type: "nav", to: "/actions?tab=growers" } },
  { id: "view-variance", label: "פערי מחירים חריגים", group: "תצוגות מהירות", icon: AlertTriangle, action: { type: "nav", to: "/actions?tab=price_variance" } },
  { id: "view-anomalies", label: "חשד למחיר/עלות", group: "תצוגות מהירות", icon: Search, action: { type: "nav", to: "/actions?tab=cost_anomalies" } },
  { id: "view-marked", label: "פריטים שסימנתי", group: "תצוגות מהירות", icon: Receipt, action: { type: "nav", to: "/actions?tab=marked" } },
  { id: "view-stockouts", label: "פריטים חסרים במלאי", group: "תצוגות מהירות", icon: AlertTriangle, action: { type: "nav", to: "/inventory" }, keywords: ["stockout", "חסר"] },
  { id: "view-dead-stock", label: "הון תקוע (Dead Stock)", group: "תצוגות מהירות", icon: AlertTriangle, action: { type: "nav", to: "/inventory" }, keywords: ["dead", "תקוע"] },

  // Insight stories
  { id: "story-branches", label: "השוואת סניפים", group: "סיפורים עסקיים", icon: BarChart3, action: { type: "nav", to: "/insights#branches" } },
  { id: "story-top", label: "Top Sellers", group: "סיפורים עסקיים", icon: Crown, action: { type: "nav", to: "/insights#top-sellers" } },
  { id: "story-invoices", label: "החשבוניות הגדולות", group: "סיפורים עסקיים", icon: Receipt, action: { type: "nav", to: "/insights#top-invoices" } },

  // Ask AI shortcuts
  { id: "ai-week", label: "מה עלה השבוע?", group: "שאל את ה-AI", icon: Sparkles, action: { type: "ai", q: "מה עלה השבוע?" } },
  { id: "ai-loss", label: "איפה אני מאבד כסף?", group: "שאל את ה-AI", icon: Sparkles, action: { type: "ai", q: "איפה אני מאבד כסף?" } },
  { id: "ai-restock", label: "מה כדאי להזמין?", group: "שאל את ה-AI", icon: Sparkles, action: { type: "ai", q: "מה כדאי להזמין החודש?" } },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [skus, setSkus] = useState([]);
  const [skuLoading, setSkuLoading] = useState(false);
  const inputRef = useRef(null);
  const skuAbortRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { send } = useChat();

  // Cmd/Ctrl+K toggle
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
      track("command_palette_open", location.pathname, {});
    }
  }, [open, location.pathname]);

  // Live SKU search — fires when query is ≥ 2 chars, debounced 250ms
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setSkus([]);
      setSkuLoading(false);
      return;
    }
    setSkuLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await searchSkus(q, 6);
        setSkus(data.results || []);
      } catch {
        setSkus([]);
      } finally {
        setSkuLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  // Filter commands by query (Hebrew + English keywords)
  const filtered = useMemo(() => {
    if (!query.trim()) return COMMANDS;
    const q = query.trim().toLowerCase();
    return COMMANDS.filter((c) => {
      const hay = [c.label, c.group, ...(c.keywords || [])].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [query]);

  // Group results
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((c) => {
      if (!g[c.group]) g[c.group] = [];
      g[c.group].push(c);
    });
    return g;
  }, [filtered]);

  // Compute "flat" list for keyboard navigation
  const flatList = useMemo(() => filtered, [filtered]);

  const runCommand = (cmd) => {
    track("command_palette_run", location.pathname, { id: cmd.id });
    setOpen(false);
    if (cmd.action.type === "nav") {
      navigate(cmd.action.to);
    } else if (cmd.action.type === "ai") {
      askViaRail(cmd.action.q);
    }
  };

  const askViaRail = (q) => {
    // On /chat the rail is unmounted — fall back to local ChatContext send.
    // Anywhere else, dispatch the event so the side rail (or mobile sheet)
    // picks it up and stays in context with the user's current page.
    if (location.pathname.startsWith("/chat")) {
      send(q, "command-palette");
    } else {
      window.dispatchEvent(
        new CustomEvent("ai-copilot-ask", { detail: { prompt: q } })
      );
    }
  };

  // Free-text "ask AI" if no matches and user types something
  const askAIDirect = () => {
    const q = query.trim();
    if (!q) return;
    track("command_palette_ask_ai", location.pathname, { q });
    setOpen(false);
    askViaRail(q);
  };

  const handleKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, flatList.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flatList[activeIdx]) {
        runCommand(flatList[activeIdx]);
      } else if (query.trim()) {
        askAIDirect();
      }
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[90] flex items-start justify-center pt-[12vh] px-4"
          data-testid="command-palette"
        >
          <div
            className="absolute inset-0 bg-black/75 backdrop-blur-md"
            onClick={() => setOpen(false)}
          />
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.985 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-xl bg-card border border-border rounded-lg shadow-2xl overflow-hidden"
            data-testid="command-palette-dialog"
          >
            {/* Ambient glow */}
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-primary/8 rounded-full blur-3xl pointer-events-none" />

            {/* Search input */}
            <div className="relative flex items-center gap-3 px-4 py-3.5 border-b border-border">
              <Search className="w-4 h-4 text-zinc-500 shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIdx(0);
                }}
                onKeyDown={handleKey}
                placeholder="חפש, נווט או שאל את ה-AI..."
                className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder-zinc-600"
                data-testid="command-palette-input"
              />
              <div className="flex items-center gap-1 shrink-0">
                <kbd className="text-[10px] font-mono text-zinc-600 bg-[#0A0A0E] border border-border rounded px-1.5 py-0.5">esc</kbd>
              </div>
            </div>

            {/* Results */}
            <div className="relative max-h-[55vh] overflow-y-auto rail-scroll p-2">
              {/* Live SKU results (when query >= 2 chars) */}
              {query.trim().length >= 2 && (
                <div className="mb-2">
                  <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono px-3 py-1.5 flex items-center justify-between">
                    <span>פריטים ({skus.length})</span>
                    {skuLoading && <span className="text-primary normal-case animate-pulse">מחפש...</span>}
                  </div>
                  {skus.length === 0 && !skuLoading && (
                    <div className="px-3 py-2 text-[11px] text-zinc-600">לא נמצאו פריטים תואמים.</div>
                  )}
                  {skus.map((sku) => (
                    <button
                      key={sku.sku}
                      onClick={() => {
                        track("command_palette_sku_click", location.pathname, { sku: sku.sku });
                        setOpen(false);
                        askViaRail(`ספר לי על "${sku.product}"`);
                      }}
                      className="w-full text-right flex items-center gap-3 px-3 py-2 rounded-md hover:bg-white/[0.03] transition-colors"
                      data-testid={`sku-result-${sku.sku}`}
                    >
                      <div className="w-6 h-6 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                        <span className="text-[9px] text-primary">SKU</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-foreground truncate">{sku.product}</div>
                        <div className="text-[10px] text-zinc-500 font-mono mt-0.5 truncate">
                          {sku.sku} · ₪{Math.round(sku.revenue).toLocaleString()} · {sku.invoices} חשבוניות
                        </div>
                      </div>
                      <ArrowLeft className="w-3 h-3 text-zinc-700" />
                    </button>
                  ))}
                </div>
              )}

              {flatList.length === 0 ? (
                <div className="px-3 py-8 text-center">
                  <Sparkles className="w-5 h-5 text-primary/70 mx-auto mb-3" />
                  <div className="text-sm text-zinc-400 mb-3">
                    אין התאמה — שלח את <span className="text-foreground font-medium">"{query}"</span> ישירות ל-AI?
                  </div>
                  <button
                    onClick={askAIDirect}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                    data-testid="command-palette-ask-ai"
                  >
                    שאל את Optygo Pulse <ArrowLeft className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                Object.entries(grouped).map(([groupName, items]) => (
                  <div key={groupName} className="mb-2 last:mb-0">
                    <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono px-3 py-1.5">
                      {groupName}
                    </div>
                    {items.map((cmd) => {
                      const idx = flatList.indexOf(cmd);
                      const active = idx === activeIdx;
                      return (
                        <button
                          key={cmd.id}
                          onClick={() => runCommand(cmd)}
                          onMouseEnter={() => setActiveIdx(idx)}
                          className={`w-full text-right flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                            active
                              ? "bg-primary/10 text-foreground"
                              : "text-zinc-300 hover:bg-white/[0.03]"
                          }`}
                          data-testid={`command-${cmd.id}`}
                        >
                          <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${
                            active ? "bg-primary/15 text-primary" : "bg-[#0A0A0E] text-zinc-500 border border-border"
                          }`}>
                            <cmd.icon className="w-3 h-3" strokeWidth={1.75} />
                          </div>
                          <span className="flex-1 text-sm">{cmd.label}</span>
                          {active && <ArrowLeft className="w-3 h-3 text-primary" />}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Footer hint */}
            <div className="relative px-4 py-2.5 border-t border-border bg-[#0A0A0E]/40 flex items-center justify-between text-[10px] text-zinc-600 font-mono">
              <div className="flex items-center gap-2">
                <span>↑↓ ניווט</span>
                <span>·</span>
                <span>↵ פתח</span>
                <span>·</span>
                <span>esc סגור</span>
              </div>
              <div className="flex items-center gap-1">
                <ShieldCheck className="w-2.5 h-2.5 text-primary" />
                <span>נתונים מ-Priority</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* Trigger button — small "⌘K" chip you can drop in toolbars / sidebars */
export function CommandKBadge({ className = "" }) {
  const isMac = typeof window !== "undefined" && /Mac/.test(navigator.platform);
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: isMac, ctrlKey: !isMac }))}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-card hover:border-primary/30 hover:text-primary text-zinc-400 transition-colors ${className}`}
      data-testid="command-k-trigger"
    >
      <Search className="w-3 h-3" />
      <span className="text-[11px]">חיפוש</span>
      <kbd className="text-[9px] font-mono text-zinc-600 bg-[#0A0A0E] border border-border rounded px-1 py-px ms-1">
        <Command className="w-2.5 h-2.5 inline-block -mt-px" />K
      </kbd>
    </button>
  );
}
